import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../lib/auth';
import { connectionApi, statusLabel, type Connection, type ConnectionSetupDraft } from '../../lib/connections-api';
import { ConnectionSetupForm } from './ConnectionSetupForm';
import { liveState, useReadinessClock } from './connection-state';
import styles from './Connections.module.css';

export function ConnectionSetupCard({ setupId }: { setupId: string }) {
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  return <ScopedSetupCard key={`${scope}:${setupId}`} setupId={setupId} scope={scope} />;
}

function ScopedSetupCard({ setupId, scope }: { setupId: string; scope: string }) {
  const cache = useQueryClient();
  const key = ['connection-setup', scope, setupId];
  const [collapsed, setCollapsed] = useState(false);
  useReadinessClock();
  const query = useQuery({ queryKey: key, queryFn: () => connectionApi.setupDraft(setupId), retry: false });
  const cancel = useMutation({ mutationFn: () => connectionApi.cancelSetup(setupId),
    onSuccess: draft => cache.setQueryData(key, draft) });
  const remember = (connection: Connection) => cache.setQueryData<ConnectionSetupDraft>(key,
    old => old ? { ...old, connection } : old);
  if (!query.data) return <section className={styles.card} aria-label="Connection setup">
    {query.isError ? <><p role="alert">{query.error.message}</p><button onClick={() => void query.refetch()}>Retry loading setup</button></>
      : <p role="status">Loading connection setup…</p>}
  </section>;
  const draft = query.data;
  if (draft.cancelled || draft.removed || draft.connection?.disconnected_at) return <section className={styles.card}>
    <strong>{draft.cancelled ? 'Connection setup cancelled' : 'Connection removed'}</strong>
    <p>Ask Echo to prepare a new setup if you want to connect again.</p>
  </section>;
  return <section className={styles.setupCard} aria-label="Connection setup" aria-busy={cancel.isPending}>
    {collapsed && draft.connection ? <div className={styles.card}>
      <strong>{draft.connection.name} · {statusLabel(liveState(draft.connection))}</strong>
      <button onClick={() => { setCollapsed(false); void query.refetch(); }}>Review connection</button>
    </div> : <fieldset className={styles.setupFields} disabled={cancel.isPending}>
      <ConnectionSetupForm starter={draft.defaults.starter} defaults={draft.defaults} setupDraftId={setupId}
        {...(draft.connection ? { initialConnection: draft.connection } : {})} inline onProgress={remember}
        onSaved={connection => { remember(connection); setCollapsed(true); }}
        onClose={() => cancel.mutate()} />
    </fieldset>}
    {draft.connection && <Link to="/app/connections/$connectionId" params={{ connectionId: draft.connection.id }}>View in External connections</Link>}
    {cancel.isError && <p role="alert">{cancel.error.message}</p>}
  </section>;
}
